import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import DefaultSeoCleanup from "@/components/DefaultSeoCleanup";
import { lazy, Suspense, useEffect } from "react";
import HomePage from "@/pages/HomePage";
import { additionalSeoCitySlugs } from "@/data/citySlugs";
import { trackEvent } from "@/lib/analytics";
import { installAdminAuthInterceptor } from "@/utils/adminSession";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
installAdminAuthInterceptor();

const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const FormulasPage = lazy(() => import("@/pages/FormulasPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ComparatifDevisPage = lazy(() => import("@/pages/ComparatifDevisPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const LegacyRouteBlockedPage = lazy(() => import("@/pages/LegacyRouteBlockedPage"));
const MovingGuidePage = lazy(() => import("@/pages/MovingGuidePage"));
const ConfirmationPage = lazy(() => import("@/pages/ConfirmationPage"));
const DemenagementParis = lazy(() => import("@/pages/DemenagementParis"));
const MentionsLegales = lazy(() => import("@/pages/MentionsLegales"));
const ConfidentialiteCookies = lazy(() => import("@/pages/ConfidentialiteCookies"));
const SuppressionDonnees = lazy(() => import("@/pages/SuppressionDonnees"));
const TermesConditions = lazy(() => import("@/pages/TermesConditions"));
const DemenagementLongueDistance = lazy(() => import("@/pages/DemenagementLongueDistance"));
const DemenagementIleDeFrance = lazy(() => import("@/pages/DemenagementIleDeFrance"));
const DemenagementLevallois = lazy(() => import("@/pages/DemenagementLevallois"));
const DemenagementNeuilly = lazy(() => import("@/pages/DemenagementNeuilly"));
const DemenagementPuteaux = lazy(() => import("@/pages/DemenagementPuteaux"));
const DemenagementBoisColombes = lazy(() => import("@/pages/DemenagementBoisColombes"));
const DemenagementClichy = lazy(() => import("@/pages/DemenagementClichy"));
const DemenagementArgenteuil = lazy(() => import("@/pages/DemenagementArgenteuil"));
const DemenagementBezons = lazy(() => import("@/pages/DemenagementBezons"));
const DemenagementColombes = lazy(() => import("@/pages/DemenagementColombes"));
const DemenagementCourbevoie = lazy(() => import("@/pages/DemenagementCourbevoie"));
const DemenagementNanterre = lazy(() => import("@/pages/DemenagementNanterre"));
const DemenagementSaintDenis = lazy(() => import("@/pages/DemenagementSaintDenis"));
const DemenagementMontreuil = lazy(() => import("@/pages/DemenagementMontreuil"));
const DemenagementChatou = lazy(() => import("@/pages/DemenagementChatou"));
const DemenagementGarches = lazy(() => import("@/pages/DemenagementGarches"));
const DemenagementVaucresson = lazy(() => import("@/pages/DemenagementVaucresson"));
const DemenagementEntreprisesPage = lazy(() => import("@/pages/DemenagementEntreprisesPage"));
const MonteMeublesPage = lazy(() => import("@/pages/services/MonteMeublesPage"));
const DemenagementParticuliersPage = lazy(() => import("@/pages/services/DemenagementParticuliersPage"));
const DebarrasPage = lazy(() => import("@/pages/services/DebarrasPage"));
const GardeMeublesPage = lazy(() => import("@/pages/services/GardeMeublesPage"));
const StatsPage = lazy(() => import("@/pages/StatsPage"));
const VisitesTechniquesPage = lazy(() => import("@/pages/VisitesTechniquesPage"));
const LivraisonFournituresPage = lazy(() => import("@/pages/LivraisonFournituresPage"));
const AiAgentPage = lazy(() => import("@/pages/admin/AiAgentPage"));
const AiTestPage = lazy(() => import("@/pages/admin/AiTestPage"));
const TasksPage = lazy(() => import("@/pages/admin/TasksPage"));
const AotPage = lazy(() => import("@/pages/admin/AotPage"));
const PointagePage = lazy(() => import("@/pages/admin/PointagePage"));
const ChronoPage = lazy(() => import("@/pages/ChronoPage"));
const ChronoConfirmationPage = lazy(() => import("@/pages/ChronoConfirmationPage"));
const ChronoAdsConfirmationPage = lazy(() => import("@/pages/ChronoAdsConfirmationPage"));
const ChronoAdminPage = lazy(() => import("@/pages/ChronoAdminPage"));
const ChronoCgvPage = lazy(() => import("@/pages/ChronoCgvPage"));
const ChronoDriverPage = lazy(() => import("@/pages/ChronoDriverPage"));
const ChronoTrackingPage = lazy(() => import("@/pages/ChronoTrackingPage"));
const SignaturePage = lazy(() => import("@/pages/SignaturePage"));
const EstimationInteretPage = lazy(() => import("@/pages/EstimationInteretPage"));
const ValueDeclarationPage = lazy(() => import("@/pages/ValueDeclarationPage"));
const PlanningPage = lazy(() => import("@/pages/admin/PlanningPage"));
const RelancePage = lazy(() => import("@/pages/admin/RelancePage"));
const MaintenancePage = lazy(() => import("@/pages/admin/MaintenancePage"));
const FacturationPage = lazy(() => import("@/pages/admin/FacturationPage"));
const RexAdminPage = lazy(() => import("@/pages/admin/RexAdminPage"));
const ScheduledMailsPage = lazy(() => import("@/pages/admin/ScheduledMailsPage"));
const EmailTemplatesPage = lazy(() => import("@/pages/admin/EmailTemplatesPage"));
const RexPage = lazy(() => import("@/pages/RexPage"));

const additionalSeoCityRoutes = additionalSeoCitySlugs.map((slug) => ({
  path: slug,
  slug,
}));

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname !== '/admin' && location.pathname !== '/stats' && !location.pathname.startsWith('/admin/') && !location.pathname.startsWith('/signature-devis/') && !location.pathname.startsWith('/declaration-valeur/')) {
      trackEvent('page_view');
    }
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <DefaultSeoCleanup />
          <PageViewTracker />
          <RouteErrorBoundary>
          <Suspense fallback={<div className="min-h-screen bg-slate-900" />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/nos-services" element={<ServicesPage />} />
            <Route path="/nos-services/demenagement-entreprises" element={<DemenagementEntreprisesPage />} />
            <Route path="/nos-services/monte-meubles" element={<MonteMeublesPage />} />
            <Route path="/nos-services/demenagement-particuliers" element={<DemenagementParticuliersPage />} />
            <Route path="/nos-services/debarras" element={<DebarrasPage />} />
            <Route path="/nos-services/garde-meubles" element={<GardeMeublesPage />} />
            <Route path="/nos-formules" element={<FormulasPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/comparatif-devis" element={<ComparatifDevisPage />} />
            <Route path="/confirmation-demande" element={<ConfirmationPage />} />
            <Route path="/mon-guide-pour-demenager" element={<MovingGuidePage />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/confidentialite-cookies" element={<ConfidentialiteCookies />} />
            <Route path="/suppression-donnees" element={<SuppressionDonnees />} />
            <Route path="/cgv" element={<TermesConditions />} />
            <Route path="/demenagement-longue-distance-paris" element={<DemenagementLongueDistance />} />
            <Route path="/demenagement-ile-de-france" element={<DemenagementIleDeFrance />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/planning" element={<PlanningPage />} />
            <Route path="/admin/relances" element={<RelancePage />} />
            <Route path="/admin/maintenance" element={<MaintenancePage />} />
            <Route path="/admin/mails" element={<ScheduledMailsPage />} />
            <Route path="/admin/maintenance/mails" element={<EmailTemplatesPage />} />
            <Route path="/admin/facturation" element={<FacturationPage />} />
            <Route path="/admin/rex" element={<RexAdminPage />} />
            <Route path="/avis/:token" element={<RexPage />} />
            <Route path="/admin/visites" element={<VisitesTechniquesPage />} />
            <Route path="/admin/livraisons" element={<LivraisonFournituresPage />} />
            <Route path="/admin/ai" element={<AiAgentPage />} />
            <Route path="/admin/ai/test" element={<AiTestPage />} />
            <Route path="/admin/taches" element={<TasksPage />} />
            <Route path="/admin/aot" element={<AotPage />} />
            <Route path="/admin/pointage" element={<PointagePage />} />
            <Route path="/chrono" element={<ChronoPage />} />
            <Route path="/confirmation-chrono" element={<ChronoAdsConfirmationPage />} />
            <Route path="/chrono/confirmation" element={<ChronoConfirmationPage />} />
            <Route path="/chrono/cgv" element={<ChronoCgvPage />} />
            <Route path="/chrono/chauffeur" element={<ChronoDriverPage />} />
            <Route path="/chrono/suivi" element={<ChronoTrackingPage />} />
            <Route path="/chrono/suivi/:token" element={<ChronoTrackingPage />} />
            <Route path="/admin/chrono" element={<ChronoAdminPage />} />
            <Route path="/stats" element={<StatsPage />} />
            {/* Signature électronique des devis (lien public sécurisé) */}
            <Route path="/signature-devis/:token" element={<SignaturePage />} />
            <Route path="/estimation/interet/:token" element={<EstimationInteretPage />} />
            {/* Déclaration de valeur post-signature (lien public sécurisé) */}
            <Route path="/declaration-valeur/:token" element={<ValueDeclarationPage />} />
            {/* Legacy / sensitive routes */}
            <Route path="/wp-admin" element={<LegacyRouteBlockedPage />} />
            <Route path="/wp-admin/*" element={<LegacyRouteBlockedPage />} />
            <Route path="/wp-login.php" element={<LegacyRouteBlockedPage />} />
            <Route path="/wp-json" element={<LegacyRouteBlockedPage />} />
            <Route path="/wp-json/*" element={<LegacyRouteBlockedPage />} />
            <Route path="/xmlrpc.php" element={<LegacyRouteBlockedPage />} />
            <Route path="/.env" element={<LegacyRouteBlockedPage />} />
            <Route path="/.git" element={<LegacyRouteBlockedPage />} />
            <Route path="/.git/*" element={<LegacyRouteBlockedPage />} />
            <Route path="/vendor/*" element={<LegacyRouteBlockedPage />} />
            <Route path="/wp-content/*" element={<LegacyRouteBlockedPage />} />
            <Route path="/wp-includes/*" element={<LegacyRouteBlockedPage />} />
            {/* Pages SEO villes */}
            <Route path="/demenagement-paris" element={<DemenagementParis />} />
            <Route path="/demenagement-levallois-perret" element={<DemenagementLevallois />} />
            <Route path="/demenagement-neuilly-sur-seine" element={<DemenagementNeuilly />} />
            <Route path="/demenagement-puteaux" element={<DemenagementPuteaux />} />
            <Route path="/demenagement-bois-colombes" element={<DemenagementBoisColombes />} />
            <Route path="/demenagement-clichy" element={<DemenagementClichy />} />
            <Route path="/demenagement-colombes" element={<DemenagementColombes />} />
            <Route path="/demenagement-courbevoie" element={<DemenagementCourbevoie />} />
            <Route path="/demenagement-nanterre" element={<DemenagementNanterre />} />
            <Route path="/demenagement-argenteuil" element={<DemenagementArgenteuil />} />
            <Route path="/demenagement-bezons" element={<DemenagementBezons />} />
            <Route path="/demenagement-saint-denis" element={<DemenagementSaintDenis />} />
            <Route path="/demenagement-montreuil" element={<DemenagementMontreuil />} />
            <Route path="/demenagement-chatou" element={<DemenagementChatou />} />
            <Route path="/demenagement-garches" element={<DemenagementGarches />} />
            <Route path="/demenagement-vaucresson" element={<DemenagementVaucresson />} />
            {/* Pages SEO villes additionnelles */}
            {additionalSeoCityRoutes.map(({ path, slug }) => (
              <Route
                key={path}
                path={path}
                element={<CityPage slug={slug} />}
              />
            ))}
          </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </HelmetProvider>
  );
}

export default App;
